import * as React from 'react'
import { FlatList, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import TextInput from '@components/textInput/TextInput'
import Loader from '@components/loader/Loader'
import SortSelector from '@components/modal/SortSelector'

import useReducer from '@store/useReducer'
import * as SocieteStore from '@store/societe/societe'
import * as SiteStore from '@store/site/site'
import * as MetierStore from '@store/metier/metier'
// import * as FiltreStore from '@store/filtre/filtre'

import api from '@api/api'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'
import useDebounce from '@utils/useDebounce'

import sanitize from '@utils/Sanitizer'
import Logger from '@utils/logger'
import { sortSocietesBy } from './utils'

const ITEM_WIDTH = 370
const DRAWER_WIDTH = 300

interface PrestaProps {
  societe: SocieteResponse
  stats?: ControleQualiteStatistiqueResponse
}

const PrestaCard = ({ societe, stats }: PrestaProps) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  return (
    <Card activeOpacity={0.8} onPress={() => navigation.push(`/controles/details/infos?societeId=${societe.id}`)}>
      <Title>{societe.nameSociete}</Title>
      {!!stats && (!!stats.tauxConformiteMoyen || stats.tauxConformiteMoyen === 0) ? (
        <ScoreContainer>
          <ScoreInfo>{i18n.t('screens.controles.averageScore')}</ScoreInfo>
          <Score>{`${Math.round(stats.tauxConformiteMoyen)}%`}</Score>
        </ScoreContainer>
      ) : (
        <EmptyScoreContainer />
      )}
    </Card>
  )
}

const Presta = () => {
  const i18n = useI18n()
  const [{ width }, bp] = useBreakpoint()

  const columns = bp === 'phone' ? 1 : Math.floor((width - DRAWER_WIDTH) / ITEM_WIDTH)

  const [search, debounced, setSearch] = useDebounce('')
  const [loading, setLoading] = React.useState(true)
  const [stats, setStats] = React.useState<Record<string, ControleQualiteStatistiqueResponse>>({})

  const [sort, setSort] = React.useState<'name' | 'score'>('name')
  const [revert, setRevert] = React.useState(false)

  const site = useReducer(SiteStore.store, (s) => s.site)

  const metiers = useReducer(MetierStore.store, (s) => s.metiers)

  const societes = useReducer(SocieteStore.store, (s) => s.services)
  const [audited, setAudited] = React.useState<ControleQualiteBasicSocieteResponse[]>([])

  const services = React.useMemo(() => {
    const societeWithMetier = metiers.flatMap((m) => m.societeIds).filter((id, idx, tab) => tab.indexOf(id) === idx)
    const auditedIds = audited.map((a) => a.id)

    // Sociétés activées + qui sont liées à au moins un métier OU qui ont déjà un CQ
    return societes
      .filter((s) => (s.enabled && societeWithMetier.includes(s.id)) || auditedIds.includes(s.id))
      .filter((s) => sanitize(s.nameSociete).includes(sanitize(debounced)))
  }, [societes, debounced, metiers, audited])

  const data = React.useMemo(
    () => services.sort((a, b) => sortSocietesBy(a, b, stats[a.id], stats[b.id], sort, revert)),
    [services, stats, sort, revert]
  )

  const options = ['name', 'score'].map((key) => ({ label: i18n.t(`screens.controles.sort.${key}`), value: key }))
  const option = options.find((opt) => opt.value === sort)

  React.useEffect(() => {
    if (site) {
      api.controle.filtres
        .societe(site.id)
        .then((assos) => {
          setAudited(assos)
          return Promise.all(assos.map((s) => api.controle.stats({ societeId: s.id, siteId: site.id }))).then((res) => {
            const societesStats = res.reduce(
              (acc, cur, idx) => ({ ...acc, [assos[idx].id]: cur }),
              {} as Record<string, ControleQualiteStatistiqueResponse>
            )

            setStats(societesStats)
          })
        })
        .catch(Logger.error)
        .finally(() => setLoading(false))
    }
  }, [site])

  const renderItem = ({ item }: { item: SocieteResponse }) => {
    const stat = stats[item.id]

    return <PrestaCard societe={item} stats={stat} />
  }

  return (
    <MainContainer>
      <SearchContainer>
        <TextInput
          value={search}
          onChange={setSearch}
          placeholder={i18n.t('screens.controles.form.presta.search')}
          left={{ name: 'search' }}
          searchStyle="onLight"
        />
      </SearchContainer>

      <Sort>
        <SortSelector
          sorts={options}
          sort={option}
          reverse={revert}
          onOrderChange={() => setRevert(!revert)}
          onSortChange={(opt) => {
            if (opt) {
              setSort(opt.value)
            }
          }}
        />
      </Sort>

      {loading ? (
        <Loading>
          <Loader />
        </Loading>
      ) : data.length === 0 ? (
        <NoContent>{i18n.t('screens.controles.form.presta.empty')}</NoContent>
      ) : (
        <FlatList
          key={columns}
          data={data}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          numColumns={columns}
          contentContainerStyle={{ paddingHorizontal: bp === 'phone' ? 16 : 24 }}
          extraData={stats}
        />
      )}
    </MainContainer>
  )
}

export default Presta

const MainContainer = styled(View)`
  flex: 1;
`

const Loading = styled(View)`
  flex: 1;
  justify-content: center;
  align-items: center;
`

const SearchContainer = styled(View)`
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
`

const Card = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  border-radius: 8px;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
  ${(props) => props.theme.breakpoint !== 'phone' && `width: ${ITEM_WIDTH}px;`}
  margin: 8px 0;
  ${(props) => props.theme.breakpoint !== 'phone' && 'margin-right: 16px'};
  background-color: ${(props) => props.theme.colors.background};
  ${(props) => props.theme.constants.shadow};
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  flex: 1;
`

const ScoreContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  padding: 8px 5px;
  margin-left: 16px;
  align-items: center;
`

const ScoreInfo = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  color: ${(props) => props.theme.colors.secondaryText};
`

const Score = styled(Text)`
  ${(props) => props.theme.fonts.Callout};
`

const NoContent = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  text-align: center;
  margin: 24px 0;
`

const Sort = styled(View)`
  align-items: flex-start;
  margin: 0 ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
  margin-bottom: ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 16)}px;
`

const EmptyScoreContainer = styled(View)`
  height: 56px;
`
